import React, { useEffect, useState, useCallback, useMemo } from "react";
import { ModuleRegistry } from "ag-grid-community";
import { AllCommunityModule, ClientSideRowModelModule, CsvExportModule } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Toast } from "../../../config/Toast";
import { toast } from "react-toastify";
import { ColumnCutRecap } from "./SalaryCutColumn";
import { FormatSalaryItem } from "../../../helper/ResponseSalary";
import Posting from "./modals/Posting";
import SalaryCutAction from "./SalaryCutAction";
import moment from "moment";
import Pagination from "../../../components/Pagination";

// Services
import RekapService from "../../../services/RecapDeduction";

ModuleRegistry.registerModules([AllCommunityModule, ClientSideRowModelModule, CsvExportModule]);

function SalaryCutRecap() {
  const [rowData, setRowData] = useState([]);
  const [loadingFilter, setLoadingFilter] = useState(false);
  const [loadingPost, setLoadingPost] = useState(false);
  const [search, setSearch] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalPage, setTotalPage] = useState(0);
  const [activeSort, setActiveSort] = useState(null);
  const [payloadSort, setPayloadSort] = useState({
    sort_by: null,
    sort_type: null,
  });

  // Modal
  const [modalPosting, setModalPosting] = useState(false);
  const limitDataPages = 10;

  const notifySuccess = useCallback((message) => toast.success(message, Toast), []);
  const notifyFailed = useCallback((message) => toast.error(message, Toast), []);

  const handleDateChange = useCallback((dates) => {
    if (dates?.length === 2) {
      setStartDate(moment(dates[0]).format("YYYY-MM-DD"));
      setEndDate(moment(dates[1]).format("YYYY-MM-DD"));
      setPageNumber(1);
    }
  }, []);

  const handleSearch = useCallback((value) => {
    setSearch(value);
    setPageNumber(1);
  }, []);

  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) return;

    setLoading(true);
    const sort_by = payloadSort?.sort_by || null;
    const sort_type = payloadSort?.sort_type || null;

    try {
      const res = await RekapService.list(pageNumber, limitDataPages, search, startDate, endDate, sort_by, sort_type);
      const { data = [], total_page = 0 } = res.data;
      const formatted = FormatSalaryItem(data, pageNumber, limitDataPages);

      setRowData(formatted);
      setTotalPage(total_page);
    } catch (err) {
      console.error(err);
      notifyFailed(err.response.data.message || "Gagal memuat data");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, pageNumber, search, notifyFailed, payloadSort]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilter = useCallback(async () => {
    if (!startDate || !endDate) return notifyFailed("Periode rentang waktu tidak boleh kosong");
    setLoadingFilter(true);
    await fetchData();
    setLoadingFilter(false);
  }, [startDate, endDate, fetchData, notifyFailed]);

  const handOpenModalPost = useCallback(() => {
    if (!startDate || !endDate) {
      return notifyFailed("Periode rentang waktu tidak boleh kosong");
    }
    setModalPosting(true);
  }, [startDate, endDate, notifyFailed]);

  const handlePost = useCallback(async () => {
    if (!startDate || !endDate) return notifyFailed("Periode rentang waktu tidak boleh kosong");
    setLoadingPost(true);
    try {
      await RekapService.post({ start_date: startDate, end_date: endDate });
      notifySuccess("Upah tukang berhasil di rekap");
      fetchData();
    } catch {
      notifyFailed("Gagal posting data");
    } finally {
      setLoadingPost(false);
    }
  }, [startDate, endDate, notifySuccess, notifyFailed, fetchData]);

  const closeAllModals = useCallback(() => {
    setModalPosting(false);
  }, []);

  const handleChangePage = useCallback(({ selected }) => {
    setPageNumber(selected + 1);
  }, []);

  const handleSortOrder = useCallback((payload) => {
    setPayloadSort(payload);
    setActiveSort(payload);
  }, []);

  const colDefs = useMemo(() => ColumnCutRecap(handleSortOrder, activeSort), [handleSortOrder, activeSort]);

  return (
    <div className="w-100 d-flex flex-column gap-3">
      {modalPosting && <Posting activeModal={modalPosting} closeModal={closeAllModals} onConfirm={handlePost} />}

      <SalaryCutAction
        rangeDate={[moment(startDate).toDate(), moment(endDate).toDate()]}
        onDateChange={(dates) => handleDateChange(dates)}
        onFilter={handleFilter}
        onPost={handOpenModalPost}
        onSearch={handleSearch}
        loadingFilter={loadingFilter}
        loadingPost={loadingPost}
      />

      <div
        className="ag-theme-alpine w-100"
        style={{
          height: 50 * rowData.length + 50 + "px",
          maxHeight: 50 * rowData.length + 50 + "px",
          minHeight: "250px",
          overflow: "auto",
        }}
      >
        <AgGridReact
          rowData={loading ? [] : rowData}
          columnDefs={colDefs}
          domLayout="normal"
          theme="legacy"
          rowHeight={50}
          headerHeight={50}
          animateRows={true}
          enableCellTextSelection={true}
          sideBar={false}
          defaultColDef={{ sortable: false, filter: false, resizable: true }}
          overlayLoadingTemplate={'<span class="ag-overlay-loading-center">Memuat data...</span>'}
          overlayNoRowsTemplate={'<span class="ag-overlay-loading-center">Tidak ada data</span>'}
          getRowStyle={(params) => {
            switch (params.data?.isPosting) {
              case 1:
                return { color: "#7041c1" };
              case 0:
                return { color: "#000000" };
              default:
                return { backgroundColor: "transparent" };
            }
          }}
        />
      </div>
      {totalPage > 0 && <Pagination pageCount={totalPage} forcePage={pageNumber - 1} changePage={handleChangePage} />}
    </div>
  );
}

export default SalaryCutRecap;
